import { ErrorHandler, importProvidersFrom, Injectable, NgModule, NgZone, provideZoneChangeDetection } from "@angular/core";
import { StatsigModule, STATSIG_INIT_CONFIG } from "@statsig/angular-bindings";
import { StatsigClient } from "@statsig/js-client";
import { AppComponent } from "./app.component";
import { BrowserModule } from "@angular/platform-browser";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { CarModule } from "./car/car.module";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { CoreModule } from "./core/core.module";
import { CarRoutingModule } from "./car/car-routing.module";
import { SavingsService } from "./car/shared/pricing-summary/savings/savings.service";
import { AnalyticsDataService } from "./core/analytics/analytics-data.service";
import { AuthInterceptor } from "./core/auth.interceptor";
import { ConfigService } from "./core/config.service";
import { DisplayOptionsService } from "./core/display-options.service";
import { GlobalErrorHandler } from "./core/global-error.handler";
import { LayoutService } from "./core/layout.service";
import { SessionData } from "./core/session-data";
import { SessionService } from "./core/session.service";
import { FeatureFlagsService } from "./shared/directives/feature-flag/feature-flag.service";
import { ErrorModule } from "./shared/error/error.module";
import { MessageBoxModule } from "./shared/message-box/message-box.module";
import { MessageBoxService } from "./shared/message-box/message-box.service";
import { SpinModule } from "./shared/spin/spin.module";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ResourceLoader } from "@angular/compiler";
import { CustomCachedResourceLoader } from "./core/custom-resource.loader";
import { GoogleMapsModule } from "@angular/google-maps";
import {
  buildStatsigInitOptions,
  buildStatsigUser,
  registerStatsigApplicationEnvironmentOnEvents,
  resolveApplicationEnvironmentTier,
  setCachedApplicationEnvironmentTier
} from "./core/analytics/statsig-environment";

const statsigConfig: { client?: StatsigClient; sdkKey: string; user: object; options: object } = {
  sdkKey: '',
  user: {},
  options: {}
};

@Injectable({
  providedIn: 'root'
})

@NgModule({
  declarations: [
    AppComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ErrorModule,
    CoreModule,
    CarModule,
    CarRoutingModule,
    GoogleMapsModule,
    MessageBoxModule,
    SpinModule,
    TranslateModule,
    NgbModule,
    StatsigModule,
  ],
  providers: [
    importProvidersFrom([
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: (httpClient: HttpClient) => new TranslateHttpLoader(httpClient, './../assets/i18n/', '.json'),
          deps: [HttpClient]
        }
      }),
    ]),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    SessionService,
    SavingsService,
    DisplayOptionsService,
    FeatureFlagsService,
    {
      provide: SessionData,
      useFactory: sessionDataFactory,
      deps: [SessionService]
    },
    {
      provide: ResourceLoader,
      useClass: CustomCachedResourceLoader,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    MessageBoxService,
    LayoutService,
    AnalyticsDataService,
    { provide: STATSIG_INIT_CONFIG, useValue: statsigConfig }
  ]
})

export class AppModule {

  constructor(
    private configService: ConfigService,
    private sessionService: SessionService,
    private displayOptionsService: DisplayOptionsService,
    private featureFlagsService: FeatureFlagsService,
    private ngZone: NgZone
  ) { }

  ngDoBootstrap(app: any) {
    this.configService.loadConfig().then(async () => {
      const sd = this.configService.getSessionData();
      const configData = this.configService.getConfigData();
      const sdkKey = configData?.statsig?.clientKey ?? '';
      const tier = resolveApplicationEnvironmentTier(configData);
      setCachedApplicationEnvironmentTier(tier);

      // Statsig (incl. autocapture) is initialized OUTSIDE the Angular zone so that the
      // global listeners/timers it attaches (clicks, scroll, page views, performance,
      // flush interval) do not trigger Angular change detection on every browser event.
      // Running inside the zone caused a change-detection storm that froze the app.
      if (sdkKey) {
        await this.ngZone.runOutsideAngular(async () => {
          const client = new StatsigClient(
            sdkKey,
            buildStatsigUser(sd),
            buildStatsigInitOptions(tier)
          );
          registerStatsigApplicationEnvironmentOnEvents(client);
          await client.initializeAsync();
          statsigConfig.client = client;
        });
      }

      this.sessionService.initialize(this.configService.getSessionData());
      this.displayOptionsService.initialize(this.configService.getDisplayOptions());
      this.featureFlagsService.initialize(this.configService.getFeatureFlags());
      app.bootstrap(AppComponent);
    });
  }

}

export function sessionDataFactory(sessionService: SessionService): SessionData {
  return sessionService.getSessionData();
}
